import React from "react";
import { cn } from "@/lib/utils";

interface CardProps {
  children: React.ReactNode;
  className?: string;
  variant?: "default" | "feature" | "form";
  footer?: React.ReactNode;
  title?: string;
  titleCaption?: string;
}

export function Card({
  children,
  className,
  variant = "default",
  footer,
  title,
  titleCaption,
}: CardProps) {
  const baseClasses =
    "bg-gothia-cream rounded-lg backdrop-blur-sm border border-gothia-gold/30 h-full flex flex-col";

  const variantClasses = {
    default: "p-6",
    feature: "p-6",
    form: "p-6",
  };

  const hasHeader = Boolean(title || titleCaption);

  return (
    <div className={cn(baseClasses, variantClasses[variant], className)}>
      {hasHeader && (
        <CardHeader title={title} titleCaption={titleCaption} />
      )}
      {children}
      {footer && (
        <CardFooter>
          <CardFooterNote>{footer}</CardFooterNote>
        </CardFooter>
      )}
    </div>
  );
}

type CardHeadingAlign = "left" | "center" | "right";
type CardHeadingSpacing = "sm" | "md";

interface CardHeadingProps {
  eyebrow?: string;
  headline: string;
  align?: CardHeadingAlign;
  spacing?: CardHeadingSpacing;
  className?: string;
  eyebrowClassName?: string;
  headlineClassName?: string;
}

const headingAlignment: Record<CardHeadingAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

const headingSpacing: Record<CardHeadingSpacing, string> = {
  sm: "space-y-1",
  md: "space-y-2",
};

const headingEyebrowDefaults = "text-headline-caption";
const headingHeadlineDefaults = "text-headline";

export function CardHeading({
  eyebrow,
  headline,
  align = "left",
  spacing = "sm",
  className,
  eyebrowClassName,
  headlineClassName,
}: CardHeadingProps) {
  return (
    <div className={cn(headingSpacing[spacing], headingAlignment[align], className)}>
      {eyebrow && (
        <div className={cn(headingEyebrowDefaults, "text-gothia-gold-dark", eyebrowClassName)}>
          {eyebrow}
        </div>
      )}
      <div className={cn(headingHeadlineDefaults, "text-gothia-gold-dark", headlineClassName)}>
        {headline}
      </div>
    </div>
  );
}

interface CardHeaderProps {
  children?: React.ReactNode;
  className?: string;
  title?: string;
  titleCaption?: string;
}

export function CardHeader({ children, className, title, titleCaption }: CardHeaderProps) {
  const content = title ? (
    <div>
      {titleCaption && (
        <div className="font-body text-sm font-semibold uppercase text-gray-700 tracking-normal">
          {titleCaption}
        </div>
      )}
      <div className="text-headline text-gothia-gold-dark">
        {title}
      </div>
    </div>
  ) : (
    children
  );

  return <div className={cn("mb-1 text-left", className)}>{content}</div>;
}

interface CardFooterNoteProps {
  children: React.ReactNode;
  className?: string;
}

export function CardFooterNote({ children, className }: CardFooterNoteProps) {
  return (
    <p className={cn("font-body text-xs leading-snug text-gray-600", className)}>
      {children}
    </p>
  );
}

interface CardTitleProps {
  children: React.ReactNode;
  className?: string;
  level?: "h3" | "h4";
}

export function CardTitle({
  children,
  className,
  level = "h3",
}: CardTitleProps) {
  const baseClasses = "font-heading";
  const levelClasses = {
    h3: "text-section-title text-gothia-green",
    h4: "text-subheading text-gothia-green",
  };

  const Component = level;

  return (
    <Component className={cn(baseClasses, levelClasses[level], className)}>
      {children}
    </Component>
  );
}

interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export function CardContent({ children, className }: CardContentProps) {
  return <div className={cn("flex-grow space-y-4 text-left", className)}>{children}</div>;
}

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function CardFooter({ children, className }: CardFooterProps) {
  return (
    <div
      className={cn("mt-auto border-t border-gothia-gold/20 pt-4 text-left", className)}>
      {children}
    </div>
  );
}
